/**
 * The access screen waits for an answer (SHARING-MODEL §3.3): once someone lets this person in,
 * the file opens by itself, with no reload.
 *
 * Nothing tells the screen: the grant reaches Artwork as the project's membership, and the one
 * socket that hears about it (the notification Centre's "Access granted") keeps it to itself and
 * follows the person's notification settings. So the screen asks, lightly: whether the file opens
 * for them now (`artwork.doc.get`, the editor's own access check), every few seconds at first and
 * less often the longer nobody answers, only while the page is in front, and at once when it comes
 * back to the front. Sending a request starts the quick asking again.
 */

export const ACCESS_WATCH = {
  /** The first ask, and the first after a request is sent. */
  firstMs: 2_500,
  growth: 1.5,
  /** The longest wait while an answer is likely (just asked, or just arrived). */
  maxMs: 10_000,
  /** ...and once nobody has answered for a while. */
  idleMaxMs: 30_000,
  idleAfterMs: 5 * 60_000,
} as const;

export interface AccessWatchEnv {
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
  /** Whether the page is in front. */
  visible(): boolean;
  /** Called when it comes to the front or goes behind; returns the unsubscribe. */
  onVisibility(fn: () => void): () => void;
}

const browserEnv = (): AccessWatchEnv => ({
  now: () => Date.now(),
  setTimeout: (fn, ms) => window.setTimeout(fn, ms),
  clearTimeout: (handle) => window.clearTimeout(handle as number),
  visible: () => document.visibilityState === 'visible',
  onVisibility: (fn) => {
    document.addEventListener('visibilitychange', fn);
    return () => document.removeEventListener('visibilitychange', fn);
  },
});

export interface AccessWatch {
  /** A request was just sent: ask quickly again. */
  hurry(): void;
  stop(): void;
}

/**
 * Asks `check` until it says yes, then calls `onGranted` once and stops. A failed ask is a "not
 * yet". Never two asks at once.
 */
export function watchForAccess(check: () => Promise<boolean>, onGranted: () => void, env: AccessWatchEnv = browserEnv()): AccessWatch {
  let stopped = false;
  let asking = false;
  let timer: unknown = null;
  let delay: number = ACCESS_WATCH.firstMs;
  let since = env.now();

  const clear = () => {
    if (timer !== null) env.clearTimeout(timer);
    timer = null;
  };
  const schedule = (ms: number) => {
    clear();
    // Behind other tabs nothing is asked; coming back asks at once.
    if (!stopped && !asking && env.visible()) timer = env.setTimeout(tick, ms);
  };
  async function tick() {
    timer = null;
    if (stopped || asking || !env.visible()) return;
    asking = true;
    let ok = false;
    try {
      ok = await check();
    } catch {
      ok = false;
    }
    asking = false;
    if (stopped) return;
    if (ok) {
      stop();
      onGranted();
      return;
    }
    const cap = env.now() - since > ACCESS_WATCH.idleAfterMs ? ACCESS_WATCH.idleMaxMs : ACCESS_WATCH.maxMs;
    delay = Math.min(delay * ACCESS_WATCH.growth, cap);
    schedule(delay);
  }

  const offVisibility = env.onVisibility(() => {
    if (stopped) return;
    if (env.visible()) {
      clear();
      void tick();
    } else {
      clear();
    }
  });

  function stop() {
    stopped = true;
    clear();
    offVisibility();
  }

  schedule(delay);
  return {
    hurry() {
      if (stopped) return;
      since = env.now();
      delay = ACCESS_WATCH.firstMs;
      schedule(delay);
    },
    stop,
  };
}

/**
 * At most two opens by itself per file in two minutes. If the file is still refused after it
 * opened (the answer and the room disagree for a moment), the screen comes back and stays:
 * it never opens and closes for ever.
 */
const AUTO_OPENS = 2;
const AUTO_OPEN_WINDOW_MS = 2 * 60_000;
const autoOpened = new Map<string, number[]>();

export function mayOpenByItself(documentId: string, now = Date.now()): boolean {
  const key = documentId.toLowerCase();
  const recent = (autoOpened.get(key) ?? []).filter((at) => now - at < AUTO_OPEN_WINDOW_MS);
  if (recent.length >= AUTO_OPENS) {
    autoOpened.set(key, recent);
    return false;
  }
  autoOpened.set(key, [...recent, now]);
  return true;
}

/** Tests start clean. */
export function forgetAutoOpens(): void {
  autoOpened.clear();
}
